/**
 * 开局设定面板（2026-10-19 起拆三页）
 * 第一页 = 主角姓名（姓 / 名 / 昵称，各 ≤2 个汉字）+ 开局身份（4 颗平级单选行 + 选中行下就地展开的说明卡）；
 * 第二页 = 初始属性（单列通栏）+ 天赋（只画已解锁的槽）；
 * 第三页 = 形象创建（捏脸），确认钮在这页。
 * 捏脸为移动版精简形态：大预览（renderAvatarSVG 内联单例）+ 品类横滑标签
 * + 部件横滑条（每件＝整张脸的 PNG 小图，A 档 2026-10-04，见 `avatar/thumbs.ts`）/ 色板圆点条
 * + 鼻/嘴 Y 微调滑杆（69 批补接入，同编辑器 −6~+6 档）；
 * 联动规则、随机、清洗全部复用 avatar/config.ts，⛔ 不在本文件复刻规则逻辑。
 * 初始属性（0~20 随机）+ 随机抽取开局天赋（TAGS 表过滤）
 * 天赋槽数 = 1 + 商店已购「额外天赋槽」（成就商店解锁后购买可叠加，开局抽多个互不相同）
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { GameData } from '../../data/GameData';
import { GameConfig } from '../../core/GameConfig';
import { shopSystem } from '../../systems/ShopSystem';
import { assistantSystem } from '../../systems/AssistantSystem';
import { directSignSystem } from '../../systems/DirectSignSystem';
import { ASSISTANT } from '../../data/Assistant';
import { saveSystem } from '../../systems/SaveSystem';
import { TagDef, AttributeKey } from '../../data/types';
import { randomPlayerNameParts, deriveNickname } from '../../data/tables/NamePool';
import { attrName, createDiv, createSpan, createButton, createStatBarParts, showHint } from '../ui';
import { iconAny, IconName, FilledIconName } from '../icons';
import { resetFeedback } from '../feedback';
import { AvatarConfig, randomConfig, sanitizeConfig } from '../../avatar/config';
import { CREATE_PAGE_CATEGORIES } from '../../avatar/categories';
import { ensureSprites, avatarPngSrc } from '../../avatar/gamemount';
import { clearThumbCache } from '../../avatar/thumbs';
import { AvatarEditor } from '../AvatarEditor';

/** 身份展示顺序：素人 → 富家 → 科班 → 资源（未开放身份也展示，标注「未开放」、不可选） */
const ORIGIN_ORDER = ['TAG_014', 'TAG_013', 'TAG_001', 'TAG_012'];
const originRank = (id: string) => {
    const idx = ORIGIN_ORDER.indexOf(id);
    return idx < 0 ? 99 : idx;
};
const ALL_ORIGINS = [...GameData.TAGS.filter(t => t.pool === 'origin')]
    .sort((a, b) => originRank(a.id) - originRank(b.id));

/** 可选身份池（未解锁身份只展示、不参与选择） */
const START_ORIGINS = ALL_ORIGINS.filter(t => t.unlocked !== false);

/** 姓名三格的字符规则：只收 1~2 个汉字，⛔ 英文/数字/符号（2026-10-19 用户令） */
const NAME_MAX_CHARS = 2;
const CJK_NAME = /^[\u4e00-\u9fa5]{1,2}$/;

/**
 * 开局特性池（2026-10-21 特性点数制）：`pool:'trait'` 的全部条目。
 * 正面＝花点数（`traitPoints > 0`），负面＝回点数（`traitPoints < 0`，**只有负效果、不带任何正加成**）。
 * 玩家在建角页自由点选：点数不够的置灰，负面有张数上限（`GameConfig.TRAIT_NEGATIVE_MAX`，防退化）。
 */
const TRAIT_POOL = GameData.TAGS.filter(t => t.pool === 'trait');
/** 正面特性（花点数） */
const TRAIT_POSITIVE = TRAIT_POOL.filter(t => (t.traitPoints ?? 0) > 0);
/** 负面特性（回点数） */
const TRAIT_NEGATIVE = TRAIT_POOL.filter(t => (t.traitPoints ?? 0) < 0);

/**
 * 特性芯片上的图标（**表现层的装饰**，归 UI 层管 ⇒ 放这儿而不是数据表里；表＝数据事实的唯一来源）。
 * 站内 24 枚通用图标里挑语义贴的；缺一枚就退成同尺寸的空位（占位保对齐，⛔ 不画空框——空框看着像坏图）。
 * 图标颜色＝按钮文字色（`currentColor` 继承），正向绿、负向红。
 */
const TRAIT_ICONS: Record<string, IconName | FilledIconName> = {
    TAG_002: 'star',         // 天生丽质：颜值
    TAG_003: 'chat',         // 八面玲珑：情商
    TAG_004: 'energy',       // 体魄强健：体能
    TAG_009: 'gem',          // 气质如兰：气质
    TAG_011: 'sparklesFill', // 天生戏骨：演技（实心闪光＝戏上的光）
    TAG_015: 'dice',         // 锦鲤附体：掷骰判定
    TAG_016: 'invite',       // 自来熟：结识
    TAG_017: 'sign',         // 谈判专家：签约/条款
    TAG_018: 'trophy',       // 奖项复利：奖杯
    TAG_402: 'folder',       // 九漏鱼：书念一遍漏一遍
    TAG_403: 'megaphone',    // 五音不全：唱
    TAG_404: 'circleX',      // 低情商：话都当真
    TAG_405: 'lock',         // 无效营销：营销白做
};

/** 属性随机区间上限（决定条宽；批3-A：查属性定义表 startRange，随 ATTR_SCALE 预处理自动缩放，⛔ 不再写死 20） */
const START_MAX = GameData.ATTRIBUTE_DEFS.talent.startRange[1];

/**
 * 开局锁定的三格（用户 2026-10-05 令：开局捏脸不显示服装、眼睛和配饰，统一穿白T不能改，
 * 这三样改成后面在游戏里获得、在造型间可以换；2026-10-20 令复述并点名「开局的形象创建没有饰品和眼镜，
 * 没有衣服 衣服统一穿白T」，于是眼睛加回去、眼镜补进锁定列）。类目页签由 `categories.ts` 的 `studioOnly` 撤下，
 * 值在这里钉死：`cloth` 0＝白T（`parts.ts` CLOTHES 第一款）、`glasses` 0＝不戴、`acc` 0＝无（配饰表现只有这一枚）。
 * 注意：钉的是**建角页**这一侧，`randomConfig` 本体不许改（造型间的「随机造型」要能随出别的衣服）。
 */
const START_LOCKED_SLOTS = { cloth: 0, glasses: 0, acc: 0 } as const;

/** 建角页的一张脸＝随机一套后把锁定三格钉回去（「随机造型」与读回草图都走这里，不许随出别的衣服/眼镜/配饰） */
function startFace(base?: AvatarConfig): AvatarConfig {
    return { ...(base ?? randomConfig({ gender: 'f' })), ...START_LOCKED_SLOTS };
}

// 捏脸类目清单与联动置灰判据自 76 批起搬到 `avatar/categories.ts`（与造型间同源，不许两份镜像）

export class CharacterCreatePanel {
    readonly element: HTMLElement;
    private surInput: HTMLInputElement;
    private givenInput: HTMLInputElement;
    private nickInput: HTMLInputElement;
    private attrGrid: HTMLElement;
    private originList: HTMLElement;
    private traitList: HTMLElement;
    /** 特性点数余额的读数（段首右端那枚胶囊，剩几点/用完了） */
    private traitPointsVal!: HTMLElement;
    /**
     * 已选身份（默认＝素人出道）。
     * 2026-10-21 用户令：**未开放的身份也允许被选中**（点了就是选了），
     * 「未开放」只表现为行上的胶囊 + 「下一步」置灰，不再有「展示 ≠ 已选」两套状态。
     */
    private currentOrigin: TagDef | null = null;
    /** 已点选的特性（2026-10-21 点数制；不再随机抽） */
    private selectedTraits: TagDef[] = [];
    /** 当前展开介绍的那张特性卡（同一时刻只展开一张；点芯片就换） */
    private expandedTrait: TagDef | null = null;
    /** 第一页底部的「下一步」：选中的身份未开放时置灰 */
    private nextBtn!: HTMLButtonElement;
    /** 第二页底部的「下一步」：特性没选够最低张数时置灰 */
    private statsNextBtn!: HTMLButtonElement;

    /** 当前捏脸配置在编辑器组件里（确认时取 editor.cfg 写进 player.avatar） */
    private editor!: AvatarEditor;
    /** 0 = 姓名+身份页，1 = 属性+天赋页，2 = 形象创建页 */
    private page: 0 | 1 | 2 = 0;
    private pageIdentity!: HTMLElement;
    private pageStats!: HTMLElement;
    private pageFace!: HTMLElement;
    private nudgeRow!: HTMLElement;

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        const inner = createDiv('setup-inner');

        // 三页的退路都在底栏左位（与决策页「清空 / 确认行程」同一配方）：
        // 第一页「返回主页」、第二页「上一步」、第三页「上一步」
        this.pageIdentity = this.buildIdentityPage();
        this.pageStats = this.buildStatsPage();
        this.pageFace = this.buildFacePage();
        inner.appendChild(this.pageIdentity);
        inner.appendChild(this.pageStats);
        inner.appendChild(this.pageFace);

        panel.appendChild(inner);
        this.element = panel;
    }

    /** 第一页（标题「开局身份」）：姓名三格（白纸卡）+ 开局身份（4 颗平级单选行，选中行下就地展开说明）+ 出口行 */
    private buildIdentityPage(): HTMLElement {
        const page = createDiv('cc-page');
        const head = createDiv('cc-head', [createSpan('head-title', '开局身份')]);
        head.appendChild(this.makeSteps(0));
        page.appendChild(head);

        // 页头与底部出口行固定，中段自己滚（2026-10-21 用户令：任何页面都不整页滚，按钮钉在页底）
        const scroll = createDiv('cc-scroll');

        // 姓名三格：一行三格等宽（姓 / 名 / 昵称），标签在框上方，「随机」收成右端一颗小方钮
        // 段首标题「主角姓名」提到卡外，与下面「开局身份」同级（两个段首同一份画法）
        scroll.appendChild(createDiv('cc-sec', ['主角姓名']));
        const nameCard = createDiv('name-card');
        const nameRow = createDiv('name-row');
        this.surInput = this.makeNameField(nameRow, '姓', 'surname');
        this.givenInput = this.makeNameField(nameRow, '名', 'given');
        this.nickInput = this.makeNameField(nameRow, '昵称', 'nick');
        const nameBtn = createButton('name-rand', '', undefined, 'dice');
        nameBtn.addEventListener('click', () => this.randomName());
        nameRow.appendChild(nameBtn);
        nameCard.appendChild(nameRow);
        scroll.appendChild(nameCard);

        // 开局身份：4 颗平级单选行（点谁就是选了谁），被选中的那行下面就地展开说明白卡
        scroll.appendChild(createDiv('cc-sec', ['开局身份']));
        this.originList = createDiv('origin-list');
        scroll.appendChild(this.originList);
        page.appendChild(scroll);

        // 出口按钮固定在页面底部，不随滚动区高度跳动
        const btnRow = createDiv('btn-row');
        btnRow.appendChild(createButton('btn btn-ghost', '返回主页', () => this.app.onBackToMenu()));
        this.nextBtn = createButton('btn btn-primary', '下一步', () => this.setPage(1));
        btnRow.appendChild(this.nextBtn);
        page.appendChild(btnRow);
        return page;
    }

    /** 建角三页的步骤点（2026-10-21 E 案）：复用周点条 `.week-dots/.wd`，前 N 个点亮；
     *  active＝0/1/2（开局身份 / 角色属性 / 形象创建） */
    private makeSteps(active: number): HTMLElement {
        const steps = createDiv('week-dots');
        for (let i = 0; i < 3; i++) {
            steps.appendChild(createSpan(i <= active ? 'wd on' : 'wd'));
        }
        return steps;
    }

    /** 姓名一格：小标签在框上方 + 输入框；绑定「只留汉字、≤2 字」的输入清洗 */
    private makeNameField(parent: HTMLElement, labelText: string, fieldClass: string): HTMLInputElement {
        const field = createDiv('name-field ' + fieldClass);
        field.appendChild(createSpan('nf-label', labelText));
        const input = document.createElement('input');
        input.className = 'name-input';
        input.maxLength = NAME_MAX_CHARS;
        field.appendChild(input);
        parent.appendChild(field);
        // 中文输入法组字期间不打断（isComposing），组完/失焦时再清洗
        input.addEventListener('input', (ev) => {
            if (ev instanceof InputEvent && ev.isComposing) return;
            this.sanitizeNameInput(input);
        });
        input.addEventListener('blur', () => this.sanitizeNameInput(input));
        return input;
    }

    /** 姓名输入清洗：剔除非汉字字符并截到 NAME_MAX_CHARS 个字 */
    private sanitizeNameInput(input: HTMLInputElement): void {
        const clean = input.value.replace(/[^\u4e00-\u9fa5]/g, '').slice(0, NAME_MAX_CHARS);
        if (clean !== input.value) input.value = clean;
    }

    /** 第二页（标题「角色属性」）：初始属性（白卡，含重掷钮）+ 特性点选（白卡）+ 出口行 */
    private buildStatsPage(): HTMLElement {
        const page = createDiv('cc-page');
        const head = createDiv('cc-head', [createSpan('head-title', '角色属性')]);
        head.appendChild(this.makeSteps(1));
        page.appendChild(head);

        // 页头与底部出口行固定，中段自己滚（与第一页同一配方）
        const scroll = createDiv('cc-scroll');

        // 段一：初始属性（白卡；8 条属性单列通栏 —— 条子从 ~73px 变 ~238px，8 个数值右对齐成一条线）
        const attrCard = createDiv('setup-card');
        const attrHead = createDiv('attr-head');
        attrHead.appendChild(createDiv('attr-title', ['初始属性']));
        // 「重掷属性」按钮**坐在初始属性块里**（2026-10-21 用户令：放回块内）。
        // 特性改成点选后，它就真的只管属性了 ⇒ 放回属性块头不再有"读成只重掷属性"的问题。
        // 只留图标（方形小钮），无障碍名走 aria-label（视觉上不出现文字）。
        const rerollBtn = createButton('attr-reroll', '', () => this.reroll(), 'refresh');
        rerollBtn.setAttribute('aria-label', '重掷初始属性');
        attrHead.appendChild(rerollBtn);
        attrCard.appendChild(attrHead);
        this.attrGrid = createDiv('stat-grid');
        attrCard.appendChild(this.attrGrid);
        scroll.appendChild(attrCard);

        // 段二：特性点选（白卡；两段之间一条虚线）
        scroll.appendChild(createDiv('divider-dotted'));
        const traitCard = createDiv('setup-card');
        const traitHead = createDiv('trait-head');
        traitHead.appendChild(createSpan('trait-label', '特性'));
        this.traitPointsVal = createSpan('trait-points', '');
        traitHead.appendChild(this.traitPointsVal);
        traitCard.appendChild(traitHead);
        this.traitList = createDiv('trait-list');
        traitCard.appendChild(this.traitList);
        scroll.appendChild(traitCard);
        page.appendChild(scroll);

        const btnRow = createDiv('btn-row');
        btnRow.appendChild(createButton('btn btn-ghost', '上一步', () => this.setPage(0)));
        this.statsNextBtn = createButton('btn btn-primary', '下一步', () => this.setPage(2));
        btnRow.appendChild(this.statsNextBtn);
        page.appendChild(btnRow);
        return page;
    }

    /** 第三页：形象创建（标题 + 预览镜 + 页签/部件条/滑杆/出口那一整片通栏） */
    private buildFacePage(): HTMLElement {
        const page = createDiv('cc-page');
        const head = createDiv('cc-head', [createSpan('head-title', '形象创建')]);
        head.appendChild(this.makeSteps(2));
        page.appendChild(head);

        const faceRow = createDiv('cc-face-row');
        const previewEl = createDiv('cc-preview');
        faceRow.appendChild(previewEl);
        const side = createDiv('cc-side');
        const diceBtn = createButton('btn btn-ghost cc-dice', '随机造型', () => {
            this.editor.setConfig(startFace());
            this.editor.rerender();
        }, 'dice');
        side.appendChild(diceBtn);
        faceRow.appendChild(side);
        page.appendChild(faceRow);

        // 底部这一整片（页签 + 部件条 + 滑杆 + 确认钮）＝一条通栏底栏（用户 2026-10-04 令：
        // 「⛔ 圆角的弹窗，要像导航栏那样占据整个下半部分、⛔ 页边距」）⇒ `.cc-controls` 抄 `.tab-bar` 形制
        const controls = createDiv('cc-controls');
        const tabsEl = createDiv('cc-tabs');
        controls.appendChild(tabsEl);
        const stripEl = createDiv('cc-strip');
        controls.appendChild(stripEl);

        // 鼻/嘴 Y 微调滑杆容器（69 批补接入）：仅这两品类显示，滑杆本体由 AvatarEditor 建
        this.nudgeRow = createDiv('cc-nudge-row');
        controls.appendChild(this.nudgeRow);

        // 退路挪到这里：决策页「清空 / 确认行程」同一配方（`.btn-row`＝ghost 定宽在左 + primary 撑满在右），
        // 与工作室「离开 / 完成造型」同形 ⇒ 三处出口读法一致（用户 10-04 令删掉左上角那颗「上一步」）
        const btnRow = createDiv('btn-row');
        btnRow.appendChild(createButton('btn btn-ghost', '上一步', () => this.setPage(1)));
        btnRow.appendChild(createButton('btn btn-primary cc-confirm', '确认创建', () => this.confirm()));
        controls.appendChild(btnRow);
        page.appendChild(controls);

        // 捏脸编辑器＝公共组件（与衣帽间/商业街部件店同源；建角页无 onChange——cfg 攒到确认才落档）
        this.editor = new AvatarEditor({
            categories: CREATE_PAGE_CATEGORIES,
            preview: previewEl,
            tabs: tabsEl,
            strip: stripEl,
            nudgeRow: this.nudgeRow,
        }, startFace());
        return page;
    }

    /** 进入面板：读取当前随机属性 + 重置特性点选，刷新身份选中态 */
    refresh(): void {
        this.setPage(0);
        // 进店＝起一张新脸，上一张脸的整脸小图全部作废 ⇒ 页内缓存在此交回（LRU 也封顶，双保险）
        clearThumbCache();
        const saved = GameState.getInstance().player.avatar;
        this.editor.setConfig(saved ? startFace(sanitizeConfig(saved)) : startFace());
        this.editor.rerender();
        // 精灵未就绪时预览只有背景，加载完成后补渲一次
        ensureSprites().then(() => this.editor.rerender());
        // 特性点选：每次进建角页从头选（点数制，不再随机抽）
        this.selectedTraits = [];
        this.expandedTrait = null;
        // 身份沿用当前档（旧档可能是已锁的 legacy 身份，但未解锁身份不进建角页 ⇒ 回落素人）
        // 默认＝素人已选中（2026-10-20 用户令：开局默认选择素人出道，此时「下一步」须可点）
        const savedOrigin = GameState.getInstance().player.origin;
        this.currentOrigin = START_ORIGINS.find(t => t.id === savedOrigin) ?? START_ORIGINS[0];
        // 姓名沿用开局随机：姓氏 / 名字分开读（旧档缺字段时按「单字姓」拆全名兜底）
        const player = GameState.getInstance().player;
        const fullName = player.name || '';
        const surname = player.surname || fullName.slice(0, 1);
        const given = player.givenName || fullName.slice(1);
        this.surInput.value = surname;
        this.givenInput.value = given;
        const nick = player.nickname ?? '';
        this.nickInput.value = nick || deriveNickname(given);
        this.render();
    }

    /** 切页：三页互斥显示，滚动位置归顶（每页的退路都在自己那页的底栏左位） */
    private setPage(page: 0 | 1 | 2): void {
        this.page = page;
        this.pageIdentity.classList.toggle('on', page === 0);
        this.pageStats.classList.toggle('on', page === 1);
        this.pageFace.classList.toggle('on', page === 2);
        (this.element.querySelector('.setup-inner') as HTMLElement).scrollTop = 0;
    }

    /** 重掷初始属性（页头那颗钮：2026-10-21 点数制后，特性是玩家自己点的，⛔ 不再跟着重掷） */
    private reroll(): void {
        GameState.getInstance().rerollStartAttributes();
        // 重掷只随机基础值，会覆盖商店已购的开局属性加成 → 重新叠加
        shopSystem.applyStartAttributes();
        this.render();
    }

    /** 随机一组姓名：姓氏 + 名字分开掷，昵称随名字派生（双字取名 / 单字叠字） */
    private randomName(): void {
        const parts = randomPlayerNameParts('female');
        this.surInput.value = parts.surname;
        this.givenInput.value = parts.given;
        this.nickInput.value = deriveNickname(parts.given);
    }

    /** 特性点数预算：基础点数 + 成就商店买的额外点数 */
    private traitBudget(): number {
        return GameConfig.TRAIT_POINTS_BASE + shopSystem.extraTraitPoints();
    }

    /** 点数余额：预算 − 已选消耗（负面特性是负价 ⇒ 选中反而加余额） */
    private traitPointsLeft(): number {
        const spent = this.selectedTraits.reduce((sum, t) => sum + (t.traitPoints ?? 0), 0);
        return this.traitBudget() - spent;
    }

    /** 已选的负面特性张数（上限 `GameConfig.TRAIT_NEGATIVE_MAX`，防"全吃负面回点再全吃正面"的退化） */
    private traitNegativesPicked(): number {
        return this.selectedTraits.filter(t => (t.traitPoints ?? 0) < 0).length;
    }

    /** 点选/取消点选一项特性；点不动的（点数不够 / 负面已满额）由 UI 置灰，这里不接 */
    private toggleTrait(t: TagDef): void {
        const idx = this.selectedTraits.findIndex(x => x.id === t.id);
        if (idx >= 0) {
            this.selectedTraits.splice(idx, 1);
        } else {
            const cost = t.traitPoints ?? 0;
            if (cost > 0 && cost > this.traitPointsLeft()) return;   // 点数不够
            if (cost < 0 && this.traitNegativesPicked() >= GameConfig.TRAIT_NEGATIVE_MAX) return;  // 负面已满额
            this.selectedTraits.push(t);
        }
        this.renderTraits();
    }

    /** 渲染属性条 + 特性点选区 */
    private render(): void {
        const state = GameState.getInstance();
        this.attrGrid.innerHTML = '';
        GameData.ATTRIBUTE_KEYS.forEach(key => {
            this.attrGrid.appendChild(
                createStatBarParts(attrName(key as AttributeKey), state.getAttribute(key), START_MAX).row
            );
        });

        this.renderOrigin();
        this.renderTraits();
    }

    /**
     * 开局身份（2026-10-21 改版）：4 颗平级单选行，点谁就选谁。
     * **未开放的身份也允许被选中**（用户 10-21 令：点了就是选了，别再维持两套状态）：
     * 「未开放」只表现为行上的胶囊 + 「下一步」置灰；被选中的那一行下面就地展开说明白卡。
     */
    private renderOrigin(): void {
        const picked = this.currentOrigin;
        this.originList.innerHTML = '';
        if (!picked) {
            this.nextBtn.disabled = true;
            return;
        }
        ALL_ORIGINS.forEach(o => {
            const isSel = o.id === picked.id;
            const row = document.createElement('button');
            row.type = 'button';
            row.className = isSel ? 'origin-row selected' : 'origin-row';
            row.appendChild(createSpan('origin-row-name', o.name));
            // 未开放身份的唯一差异：名称旁多一枚「未开放」胶囊（同档外观）
            if (o.unlocked === false) row.appendChild(createSpan('origin-row-tag', '未开放'));
            row.addEventListener('click', () => {
                this.currentOrigin = o;
                this.renderOrigin();
            });
            this.originList.appendChild(row);
            if (isSel) {
                // 说明就地展开在选中的那行下面（白卡 + 左沿色条）
                const detail = createDiv('origin-detail');
                detail.appendChild(createDiv('origin-detail-desc', [o.description]));
                if (o.perk) {
                    detail.appendChild(createDiv('origin-detail-perk', [`优势 · ${o.perk}`]));
                }
                this.originList.appendChild(detail);
            }
        });

        // 选中的身份未开放 ⇒ 「下一步」置灰（选中的那行照样带「未开放」胶囊）
        this.nextBtn.disabled = picked.unlocked === false;
    }

    /**
     * 特性点选区（2026-10-21 点数制 + 用户定版式）：
     * **芯片上只有特性名字**（正向绿、负向红），点一下才展开那张卡的介绍（描述 + 效果 + 点价）；
     * 点一下＝选中（高亮），再点一下＝取消（去高亮）。同一时刻只有一张卡的介绍是展开的。
     * 负面张数到上限时其余负面芯片置灰；正面点不动时（点数不够）照样展开介绍并写明「点数不足」，但不选中。
     */
    private renderTraits(): void {
        const left = this.traitPointsLeft();
        const negs = this.traitNegativesPicked();
        // 点数读数＝「已消耗 / 总数」（2026-10-21 用户令）。
        // ⚠️ 吃了代价（回点）后**已消耗会是负数**，照实显示（用户令「那就显示负数」）——
        //   分母恒为当前额度（基础点数 ＋ 商店点数），不因回点而变。
        const total = this.traitBudget();
        const used = total - left;
        this.traitPointsVal.innerHTML = '';
        this.traitPointsVal.appendChild(createSpan('trait-points-used', String(used)));
        this.traitPointsVal.appendChild(createSpan('trait-points-sep', '/'));
        this.traitPointsVal.appendChild(createSpan('trait-points-total', String(total)));
        this.traitList.innerHTML = '';

        // **正向与代价同属一张网格、不分组**（用户 2026-10-21 令「放一起就行」）⇒ 靠颜色（绿/红）自己区分
        const grid = createDiv('trait-grid');
        TRAIT_POSITIVE.forEach(t => grid.appendChild(this.traitChip(t, left, negs)));
        TRAIT_NEGATIVE.forEach(t => grid.appendChild(this.traitChip(t, left, negs)));
        this.traitList.appendChild(grid);

        // 展开着的那张卡的介绍（紧贴芯片网格下方）
        const shown = this.expandedTrait;
        if (shown) this.traitList.appendChild(this.traitDetailCard(shown, left, negs));

        // 至少要选够最低张数才能进下一步（默认 1，对齐旧机制"开局保底 1 张"）
        this.statsNextBtn.disabled = this.selectedTraits.length < GameConfig.TRAIT_MIN_PICKED;
    }

    /** 展开着的那张卡的介绍：名称 + 点价 + 效果 + 描述（点不动时写明缺多少点/为何不能选） */
    private traitDetailCard(shown: TagDef, left: number, negs: number): HTMLElement {
        const cost = shown.traitPoints ?? 0;
        const isNeg = cost < 0;
        const selected = this.selectedTraits.some(x => x.id === shown.id);
        const affordable = cost <= left;
        const capOk = !isNeg || negs < GameConfig.TRAIT_NEGATIVE_MAX;
        const detail = createDiv('trait-detail' + (isNeg ? ' negative' : ''));
        const head = createDiv('trait-detail-head');
        head.appendChild(createSpan('trait-detail-name', shown.name));
        // 点价＝**纯文字**，颜色与纸卡正文同色（⛔ 不用胶囊：红卡上挂绿胶囊看着很怪）
        // 写法＝**这个特性的点数带正负号**：正面「特性点数 +2」、代价「特性点数 -1」（2026-10-21 用户令）
        head.appendChild(createSpan('trait-price', `特性点数 ${isNeg ? cost : '+' + cost}`));
        detail.appendChild(head);
        if (shown.perk) detail.appendChild(createDiv('trait-detail-perk', [shown.perk]));
        detail.appendChild(createDiv('trait-detail-desc', [shown.description]));
        if (!selected && !affordable) {
            detail.appendChild(createDiv('trait-detail-warn', [`点数不足（还差 ${cost - left} 点）`]));
        } else if (!selected && !capOk) {
            detail.appendChild(createDiv('trait-detail-warn', ['代价已达上限，不能再选']));
        }
        return detail;
    }

    /**
     * 一颗特性芯片：**只有名字**。正向绿、负向红；选中＝高亮（薄荷底/暖底 ＋ 描边）。
     * 点不动（点数不够 / 负面满额）时置灰（负面那档的"点不动"是"代价已满"，仍可点开看介绍）。
     */
    private traitChip(t: TagDef, left: number, negs: number): HTMLElement {
        const cost = t.traitPoints ?? 0;
        const isNeg = cost < 0;
        const selected = this.selectedTraits.some(x => x.id === t.id);
        const capOk = !isNeg || negs < GameConfig.TRAIT_NEGATIVE_MAX;
        const affordable = cost <= left;
        // 正面点不动＝点数不够（灰掉）；负面点不动＝代价已满（灰掉）
        const disabled = !selected && (isNeg ? !capOk : !affordable);

        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'trait-chip ' + (isNeg ? 'neg' : 'pos')
            + (selected ? ' selected' : '') + (disabled ? ' disabled' : '');
        // 图标（装饰，缺图标时留同尺寸空位保对齐）＋ 名字；颜色由按钮统一给（图标 currentColor 继承）
        const ic = TRAIT_ICONS[t.id];
        if (ic) {
            chip.appendChild(iconAny(ic));
        } else {
            chip.appendChild(createSpan('trait-icon-blank'));
        }
        chip.appendChild(createSpan('trait-chip-name', t.name));
        chip.addEventListener('click', () => {
            // 点一下：展开这张卡的介绍 ＋ 切换选中（再点一下＝取消选中）
            this.expandedTrait = t;
            this.toggleTrait(t);
        });
        return chip;
    }

    /** 确认（在第三页按）：写入主角姓氏/名字/昵称 + 捏脸配置，给角色加上身份与全部天赋，进入决策页 */
    private async confirm(): Promise<void> {
        const state = GameState.getInstance();
        if (!this.currentOrigin) {
            this.setPage(0);
            showHint('先选择一个开局身份');
            return;
        }
        const surname = this.surInput.value.trim();
        const given = this.givenInput.value.trim();
        const nick = this.nickInput.value.trim();
        if (!CJK_NAME.test(surname)) {
            this.setPage(0);
            showHint('姓氏填 1~2 个汉字');
            return;
        }
        if (!CJK_NAME.test(given)) {
            this.setPage(0);
            showHint('名字填 1~2 个汉字');
            return;
        }
        if (nick && !CJK_NAME.test(nick)) {
            this.setPage(0);
            showHint('昵称填 1~2 个汉字');
            return;
        }
        // 姓氏与名字分开存，全名 `name` 由两者拼出（展示/文案替换统一读 `name`）
        state.player.surname = surname;
        state.player.givenName = given;
        state.player.name = surname + given;
        // 昵称缺省派生（名字双字取名 / 单字叠字）；未派生则兜底空（小助理只叫「姐」）
        state.player.nickname = nick || deriveNickname(given);
        state.player.avatar = this.editor.cfg;
        // 建角初始装备并入部件所有权（自由出行批：初始件白送，此后买断拥有制）
        state.ensureOwned(this.editor.cfg);
        // 主角脸此刻才定档：补热缓存（NPC 脸在 newGame 已渲好，重复 prefetch 命中缓存即空转）
        state.warmAvatarCache();
        // 先挂身份再挂特性：身份发钱/解锁等一次性效果要先于特性落地
        state.setOrigin(this.currentOrigin.id);
        this.selectedTraits.forEach(tag => state.addTag(tag.id));
        // 开局设定已定剧本模式：随机池 → 现场重建随机剧本池（固定排期沿用 newGame 时的排期结果）
        if (state.player.marketMode === 'random') {
            state.refreshMarket();
        }
        // G-40：开局天赋的发钱效果晚于决策页已落定的基线，不重置局号会把它回放成一次成长
        resetFeedback();
        // 开局 intro（2026-10-25 新手引导批，文案在 ASSISTANT.introLines）：小助理连播气泡。
        // intro 带 {player} 占位符（台词表 #1）：这里先替换再 push——
        // ⛔ 走 joinTerm（它会对同一句再跑一遍替换逻辑=语义混淆）；昵称为空时 {player}，整段吃掉
        ASSISTANT.introLines.forEach(line => assistantSystem.push(assistantSystem.replacePlayer(line)));
        // 直签首单必发（同批拍板）：帮谈包装、从 Y1Q1 货架挑小角色，通知走经纪人聊天消息
        // （人脉页红点+小助理提路，呈现细节全在 DirectSignSystem.dispatch）；供给无货时静默跳过。
        // ⚠️ 必须在下面这次 save() **之前**（2026-10-25 茄子实测抓虫）：否则建角后不做任何触发
        // 写盘的操作就刷新/退出，读回来的首月档没有首单——经纪人聊天页的合同块凭空消失
        directSignSystem.issueFirstOffer();
        saveSystem.save();
        // 顶栏主角头像先出图再切页（2026-10-29 §3.4cm 三轮）：出图是本地 canvas，~几十 ms，
        // 等它完成再 show，决策页第一帧就有脸，⛔ 先露白底一帧再换脸
        await avatarPngSrc(state.player.avatar).catch(() => '');
        this.app.show('decision');
    }

    destroy(): void {
        // 无外部事件监听（⚠️ app.ts 从不回调 destroy ⇒ 缩略图缓存在 `refresh()` 进店时清）
    }
}
